/**
 * 純碁 (Jungo) の TypeScript 表現 (mixed-engine の serde 形と一致)。
 *
 * 設計の核は docs/games/jungo.md を参照。 ポイント:
 * - 黒コミ 6.5 (持碁排除)
 * - 上がり閾値 (動的): 黒 X ≥ 43 - G_b、 白 Y ≥ 37 - G_w
 *   G = Benson 独立 pass-alive system 数。 baseline は両側 1 system 仮定で
 *   黒 42 / 白 36、 自分側が分裂してると 1 段ずつ下がる (確定石が共有不可能な
 *   2 眼を別々に持つため)。
 * - 通常終局: 2 連続パス
 * - Positional Superko via Zobrist hash (position_history は u64 の集合)
 *
 * 上記の数値は仕様で固定。 mixed-engine 側と同期する必要がある場合は
 * docs/games/jungo.md と本ファイル両方を更新する。
 */

export type JungoColor = 'Empty' | 'Black' | 'White';
export type Side = 'First' | 'Second';

export const JUNGO_SIZE = 9;
export const JUNGO_KOMI = 6.5;
/** baseline 閾値 (G_self = 1 のときの値)。 分裂してると `baseline - (G - 1)` になる。 */
export const JUNGO_BLACK_MKO_BASELINE = 42;
export const JUNGO_WHITE_MKO_BASELINE = 36;

/** 動的 MKO 閾値 (mixed-engine 側と同期)。 `g = 0` は実用上発火しない。 */
export function jungoBlackMkoThreshold(g_b: number): number {
  return Math.max(0, 43 - g_b);
}
export function jungoWhiteMkoThreshold(g_w: number): number {
  return Math.max(0, 37 - g_w);
}

export interface JungoState {
  board: JungoColor[][];
  side: Side;
  move_number: number;
  pass_count: number;
  current_hash: string; // u64 を hex 16 桁 string でシリアライズ (JS Number の f64 精度ロス回避)
  position_history: string[]; // hex 16 桁 string の集合 (HashSet<u64> 由来)
  captures_black: number;
  captures_white: number;
  /// engine 側で apply 完了時に都度計算してキャッシュ済み。 client は再計算しない。
  pass_alive: PassAliveInfo;
}

export type JungoMove = 'Pass' | { Place: { row: number; col: number } };

export function isPlace(m: JungoMove): m is { Place: { row: number; col: number } } {
  return typeof m === 'object' && 'Place' in m;
}

export interface JungoOutcome {
  winner: Side | null;
  black_stones: number;
  white_stones: number;
  /// `2 × (black - white - 6.5)`: 整数比較用。 正なら黒勝ち。
  margin_x2: number;
  end_kind: 'PassAliveMko' | 'TwoPasses';
  black_pa_stones: number;
  white_pa_stones: number;
}

export interface PassAliveInfo {
  black_pa_stones: number;
  white_pa_stones: number;
  black_pa_cells: [number, number][];
  white_pa_cells: [number, number][];
  /** 独立 pass-alive system 数。 旧 state には無いことがあるので optional + 0 デフォルト。 */
  black_systems?: number;
  white_systems?: number;
}

export function legalKey(row: number, col: number): string {
  return `${row}-${col}`;
}

/**
 * スコアバー表示用フォーマッタ。
 * 戻り値例:
 *   G=1 (単一群): `黒 35 (確定 28 / 42)` / `白 30+6.5 (確定 22 / 36)`
 *   G=2 (分裂):   `黒 35 (確定 28 / 41)`  (実効閾値 = 42-1)
 *   G=3 (分裂):   `黒 35 (確定 28 / 40)`  (実効閾値 = 42-2)
 */
export function formatScoreBar(
  state: JungoState,
  pa: PassAliveInfo,
): { black: string; white: string } {
  const blackStones = state.board.flat().filter((c) => c === 'Black').length;
  const whiteStones = state.board.flat().filter((c) => c === 'White').length;
  const gB = pa.black_systems ?? 0;
  const gW = pa.white_systems ?? 0;
  const bThr = JUNGO_BLACK_MKO_BASELINE - (gB > 1 ? gB - 1 : 0);
  const wThr = JUNGO_WHITE_MKO_BASELINE - (gW > 1 ? gW - 1 : 0);
  return {
    black: `黒 ${blackStones} (確定 ${pa.black_pa_stones} / ${bThr})`,
    white: `白 ${whiteStones}+${JUNGO_KOMI} (確定 ${pa.white_pa_stones} / ${wThr})`,
  };
}

/**
 * pass-alive 石の Set 表現 (UI で「この石にマーカー描画」を引くのに使う)。
 */
export function passAliveSet(
  cells: [number, number][],
): Set<string> {
  return new Set(cells.map(([r, c]) => legalKey(r, c)));
}
